import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { getAllGames } from "../../../api/user";
import { LuDollarSign, LuEarth, LuLock } from "react-icons/lu";
import { getLeagueStatus } from "../landing/leagueStatus";
import { leagueTypeKey } from "../landing/leagueFormat";
import { memberCount } from "../landing/inviteCode";

// How long the sheet takes to slide, in ms. The panel stays mounted for exactly
// this long after `open` goes false so the exit can actually be seen — the sheet
// used to vanish on the same frame, which is what "does not slide back down"
// meant.
const SLIDE_MS = 220;

// How far down the sheet has to be dragged before letting go dismisses it.
// Same number the Create a League sheet uses, because client Rev 4 asked for the
// two to behave identically: "Make the center drag handle work and remove the X
// entirely. This sheet should work just like the Create a League and Comments
// half sheets."
const SWIPE_CLOSE_THRESHOLD = 80;

// Leagues you can still act in. A finished league is history, not a destination,
// and a cancelled one has no status to show at all — neither belongs in a
// switcher (client Rev 3: "Only show the user's Starting Soon and Live Now
// leagues. Do not show Completed leagues in this sheet.").
const isSwitchable = (game) => {
  const status = getLeagueStatus(game);
  return !!status && status.tone !== "ended";
};

// "Switch League" — the in-league way to move between the leagues you're in.
//
// Deliberately NOT the My Leagues screen. That one lives in League Play, the
// browsing environment: it lists leagues you could join, carries the app's own
// nav, and getting there means leaving the league you're in. Someone already
// inside a league doesn't want to browse — they want the league next door. So
// this is a selector, not a destination: it opens over the menu, shows only the
// leagues they belong to with the current one marked, and lands them straight
// inside whichever they pick.

const StatusLine = ({ game }) => {
  const status = getLeagueStatus(game);
  const members = memberCount(game);
  const when =
    status?.tone === "live"
      ? `Ends in ${status.countdownValue}`
      : status?.tone === "ended"
      ? "Ended"
      : status?.countdownValue
      ? `Starts in ${status.countdownValue}`
      : "";

  return (
    <span className="mt-0.5 block truncate text-xs text-secondary">
      {[when, `${members} member${members === 1 ? "" : "s"}`]
        .filter(Boolean)
        .join(" · ")}
    </span>
  );
};

// What kind of competition this row is, as one glyph.
//
// Client: "Right now, the switch competition modal is random. Some leagues show
// an image on the left; others don't. Let's fix this by making it easy to scan.
// Use a consistent glyph family... Please use the Lucide icon family
// consistently in the Switch competition modal: Cash Game: DollarSign, Private
// League: Lock, Trading League: Globe2."
//
// The organiser's uploaded image is gone from this row entirely — it is what
// made the list inconsistent, since only some leagues have one, and the glyph
// says something the image never did: which of the three kinds of competition
// this is. The image is untouched everywhere else it appears.
//
// `leagueTypeKey` is League Play's single answer to "what kind is this", the
// same one the landing pages and the details rail read, so this modal cannot end
// up disagreeing with them about a competition's type.
//
// LuEarth, not LuGlobe2: Lucide RENAMED Globe2 to Earth upstream, and
// react-icons ships the current names. Same drawing the client picked — the
// meridian globe rather than the latitude-lined LuGlobe.
const TYPE_GLYPH = {
  cash: LuDollarSign,
  private: LuLock,
  public: LuEarth,
};

// Client's measurements, verbatim: "Glyph: 22 to 24 px / Circular background:
// 48 × 48 px / Gap between icon and text: 12 px", stroke 2px, "All glyphs: dark
// teal".
//
// And the treatment, which the client worked out across two paragraphs and
// settled in the last one: "Inactive rows: white icon background, matching the
// row. Active row: pale green icon background, matching the active row... I
// would use a subtle 1 px light grey circle border on inactive rows so the icon
// area still has structure. On the active row, remove the border."
//
// So the disc is never a separate colour from the row it sits in — which is the
// whole point: "This creates one continuous active highlight instead of a pale
// green row containing a separate pale teal disc."
const TypeGlyph = ({ game, isCurrent }) => {
  const Glyph = TYPE_GLYPH[leagueTypeKey(game)] || TYPE_GLYPH.public;
  return (
    <span
      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${
        isCurrent ? "bg-veryLightGreen" : "border border-gray-200 bg-white"
      }`}
    >
      <Glyph size={24} strokeWidth={2} color="#0F4146" aria-hidden="true" />
    </span>
  );
};

const LeagueRow = ({ game, isCurrent, onPick }) => (
  /* gap-3 is the client's 12px. */
  <button
    type="button"
    onClick={() => onPick(game)}
    className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
      isCurrent ? "bg-veryLightGreen" : "hover:bg-gray-50"
    }`}
  >
    <TypeGlyph game={game} isCurrent={isCurrent} />
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2">
        <span
          className={`truncate text-sm font-semibold ${
            isCurrent ? "text-primaryColor" : "text-gray-800"
          }`}
        >
          {game.title}
        </span>
        {isCurrent && (
          <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primaryColor">
            Current
          </span>
        )}
      </span>
      <StatusLine game={game} />
    </span>
  </button>
);

/**
 * @param {object}   props
 * @param {boolean}  props.open
 * @param {string}   props.currentSlug  the league they're in right now
 * @param {Function} props.onClose      dismiss without switching
 * @param {Function} props.onSwitch     called after navigating, to close the menu
 */
const SwitchLeagueSheet = ({ open, currentSlug, onClose, onSwitch }) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: false, data: [] });
  // `rendered` keeps the panel in the DOM through its exit; `shown` drives the
  // transform. Two flags because a transition needs a frame between mount and
  // the class change, and needs the element to still exist on the way out.
  const [rendered, setRendered] = useState(open);
  const [shown, setShown] = useState(false);
  // Drag-to-dismiss, mirroring CreateLeagueModal. `dragY` is how far the finger
  // has pulled the sheet down; `dragging` suppresses the CSS transition while the
  // finger is down so the panel tracks the touch instead of easing behind it.
  const startY = useRef(null);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    // A fresh open must never inherit the offset the last drag left behind.
    setDragY(0);
    setDragging(false);
    startY.current = null;
    if (open) {
      setRendered(true);
      const id = window.requestAnimationFrame(() => setShown(true));
      return () => window.cancelAnimationFrame(id);
    }
    setShown(false);
    const t = window.setTimeout(() => setRendered(false), SLIDE_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  // Esc closes it, like every other dialog in the app. Previously the scrim was
  // the only way out.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Fetched when it opens rather than on mount: the menu is on every league
  // screen, and most opens of it never reach this sheet.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState({ loading: true, error: false, data: [] });
    getAllGames()
      .then((res) => {
        if (cancelled) return;
        setState({ loading: false, error: false, data: res?.data?.data || [] });
      })
      .catch((e) => {
        console.error("Switch league list failed:", e);
        if (!cancelled) setState({ loading: false, error: true, data: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
    setDragging(true);
  };

  const handleTouchMove = (e) => {
    if (startY.current == null) return;
    const delta = e.touches[0].clientY - startY.current;
    // Downward only: pulling up would lift the sheet off the bottom edge.
    if (delta > 0) setDragY(delta);
  };

  const handleTouchEnd = () => {
    if (startY.current == null) return;
    startY.current = null;
    setDragging(false);
    if (dragY > SWIPE_CLOSE_THRESHOLD) {
      // Let `open` going false drive the exit, so the sheet closes through the
      // same animation it uses everywhere else.
      setDragY(0);
      onClose?.();
    } else {
      // Not far enough — spring back.
      setDragY(0);
    }
  };

  if (!rendered) return null;

  // Completed and cancelled leagues are dropped before anything is drawn, so the
  // "only league you are in" message counts what the sheet actually offers.
  const leagues = state.data.filter(isSwitchable);
  const others = leagues.filter((g) => g.vanity_url !== currentSlug);

  // One handler for both footer shapes, so the two can never drift apart.
  const goToLeaguePlayHome = () => {
    navigate("/contest");
    onSwitch?.();
  };

  const pick = (game) => {
    if (game.vanity_url !== currentSlug) {
      // Straight into the league, not to its landing page — they're a member.
      navigate(`/contest/${game.vanity_url}`);
    }
    onSwitch?.();
  };

  // Portalled to <body>. It's opened from inside the sidebar, and that element
  // carries a transform (the slide-in) plus overflow-hidden — either of which
  // would make a `fixed` overlay position against the rail instead of the
  // viewport, and clip it to the rail's width.
  return createPortal(
    // Above the sidebar drawer (z-50), since it opens from inside it. A sheet on
    // a phone, a centred dialog on desktop.
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center md:justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${
          shown ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* max-h + an inner scroll area: however many leagues there are, the sheet
          stops at three quarters of the viewport and the list scrolls inside it.
          Mobile slides up from the bottom edge; desktop is a centred dialog, so
          it settles in place instead of sliding. */}
      <div
        className={`relative flex max-h-[75vh] w-full flex-col rounded-t-2xl bg-white ease-out md:max-h-[70vh] md:w-[26rem] md:rounded-2xl md:shadow-xl ${
          dragging ? "" : "transition-all"
        } ${
          shown
            ? "translate-y-0 opacity-100"
            : "translate-y-full opacity-0 md:translate-y-2"
        }`}
        style={{
          // The finger's offset rides on top of the open/close transform. While a
          // drag is in progress the transition is off (above), so the sheet tracks
          // the touch 1:1 instead of easing along behind it.
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transitionDuration: `${SLIDE_MS}ms`,
        }}
      >
        {/* The grab handle IS the control. Client Rev 4: "Make the center drag
            handle work and remove the X entirely. This sheet should work just
            like the Create a League and Comments half sheets." So the same touch
            handlers those use live here, on a target tall enough to actually hit
            — the hairline itself is 4px, which no thumb can find.
            touchAction pan-y stops the browser claiming the gesture for a scroll
            before these handlers see it.

            MOBILE ONLY, from client Rev 6: "Desktop version Switch League modal:
            remove the mobile swipe-down handle." It was always inert there — a
            centred dialog has no bottom edge to drag from and the handlers are
            touch-only — so on desktop it was a swipe affordance for a gesture
            that does not exist. */}
        <div
          className="shrink-0 cursor-grab select-none px-4 pb-1 pt-3 active:cursor-grabbing md:hidden"
          style={{ touchAction: "pan-y" }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          role="button"
          aria-label="Drag down to close"
        >
          <span className="mx-auto block h-1 w-10 rounded-full bg-[#D1D5DB]" />
        </div>

        {/* md:pt-4 replaces the height the handle used to contribute, so the
            title does not sit against the dialog's top edge on desktop. */}
        <div className="shrink-0 px-4 pb-2 pt-2 md:pt-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {/* Client 0014889: "Replace 'Switch league' with 'Switch
                  competition'. It covers cash games, public leagues, and
                  private leagues without changing the destination." So this is
                  unconditional — the sheet lists all three kinds, which is the
                  whole reason the old word was wrong. The subtitle follows it;
                  a sheet titled "Switch competition" that then says "another
                  league" would be half-renamed. */}
              <h2 className="font-montserrat text-base font-bold text-gray-800">
                Switch competition
              </h2>
              <p className="mt-0.5 text-xs text-secondary">
                Jump straight into another competition you are in.
              </p>
            </div>

            {/* Client Rev 6: "Then put 'Close' in the top right, aligned on the
                same row as Switch league. Currently, clicking outside the modal
                makes it close. Leave that as is."

                So the scrim button above is untouched and this is an ADDITIONAL
                way out — desktop's replacement for the handle it just lost.
                Mobile keeps the handle and does not show this, because Rev 4
                asked for the X to be gone from the sheet. */}
            <button
              type="button"
              onClick={onClose}
              className="hidden shrink-0 text-xs font-semibold text-secondary transition-colors hover:text-gray-800 md:block"
            >
              Close
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
          {state.loading ? (
            <div className="space-y-2 px-2 pt-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-xl bg-[#F3F4F6]" />
              ))}
            </div>
          ) : state.error ? (
            <p className="px-3 py-6 text-center text-sm text-secondary">
              Could not load your leagues. Close this and try again.
            </p>
          ) : (
            <>
              {leagues.map((g) => (
                <LeagueRow
                  key={g.id}
                  game={g}
                  isCurrent={g.vanity_url === currentSlug}
                  onPick={pick}
                />
              ))}

              {/* The way out to League Play, now PERMANENT. Client Rev 6:
                  "Make link to league play home a permanent footer action in the
                  Switch League modal: When only one league: keep This is the only
                  league you are in. Under it, change 'Find more leagues' to
                  'League Play home'. When two or more leagues: show the league
                  list, then a faint divider, then 'League Play home'."

                  It used to appear ONLY when there was nowhere to switch to, on
                  the reasoning that this sheet's job is to keep you inside the
                  league environment. The client has decided the other way, so the
                  action is always here — with the explanatory line above it kept
                  for the one-league case, and a divider standing in for it when
                  there is a list to separate it from.

                  Same destination either way: /contest is League Play home (its
                  index route renders LeaguePlayLanding). Only the label changed. */}
              {others.length === 0 ? (
                <div className="px-3 pt-4 text-center">
                  <p className="text-xs text-secondary">
                    This is the only league you are in.
                  </p>
                  <button
                    type="button"
                    onClick={goToLeaguePlayHome}
                    className="mt-1 text-xs font-semibold text-primaryColor hover:underline"
                  >
                    League Play home
                  </button>
                </div>
              ) : (
                <>
                  <div className="mx-3 mt-2 h-px bg-divider" />
                  <div className="px-3 pt-3 text-center">
                    <button
                      type="button"
                      onClick={goToLeaguePlayHome}
                      className="text-xs font-semibold text-primaryColor hover:underline"
                    >
                      League Play home
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SwitchLeagueSheet;
